import React, { useEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Stack, Divider, Paper, CircularProgress, IconButton,
  Button, Avatar,
  Chip
} from "@mui/material";
import { motion } from "framer-motion";
import {
  BarChart, Group, Update, ExpandMore, AddComment, AccountTree
} from '@mui/icons-material';
import supabase from "../../utils/supabase";
import bgImage from "../../images/top.jpg";
import CommentDialog from "../CommentDialog";  // ✅ コメントダイアログ
import UpdateDialog from "../UpdateDialog";    // ✅ 更新ログダイアログ（追加！）
import LoadingScreen from "../LoadingScreen";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { parseContentToMap, sanitizeHtml } from "../ProtectedRoute/Admin/Dialogs/DialogContributorCommentEditor";

export default function Home() {
  const { t, i18n } = useTranslation(["home", "common"]);
  const navigate = useNavigate();
  const [auditLogs, setAuditLogs] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeZone, setTimeZone] = useState("UTC");
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false); // ✅ 追加
  const [nameMap, setNameMap] = useState(new Map());
  const [actorNameMap, setActorNameMap] = useState(new Map());

  useEffect(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimeZone(tz);

    (async () => {
      let { data: auditData, error: auditErr } = await supabase
        .from("audit_log")
        .select("*")
        .order("changed_at", { ascending: false })
        .limit(5);
      if (auditErr && auditErr.code === "42703") {
        auditData = (
          await supabase
            .from("audit_log")
            .select("*")
            .order("created_at", { ascending: false })
            .limit(5)
        ).data;
      }
      console.log("Fetched recent audit logs:", auditData);
      const { data: commentData } = await supabase
        .from("recent_comments")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(5);

      const logs = auditData || [];
      setAuditLogs(logs);
      // preload scientific name display names for note logs
      const taxonIds = Array.from(
        new Set(
          logs
            .filter((l) => (l?.table_name || "").toLowerCase() === "scientific_name_docs")
            .map((l) => {
              const after = parseJsonSafe(l?.after_data);
              const before = parseJsonSafe(l?.before_data);
              return after?.scientific_name_id || before?.scientific_name_id || l?.row_id;
            })
            .filter(Boolean)
        )
      );
      if (taxonIds.length) {
        const { data: names } = await supabase
          .from("scientific_names")
          .select("id,name_spell_valid")
          .in("id", taxonIds);
        const map = new Map();
        (names || []).forEach((n) => {
          map.set(n.id, n.name_spell_valid);
        });
        setNameMap(map);
      } else {
        setNameMap(new Map());
      }

      // preload actor display names from admin_team_members
      const actorIds = Array.from(new Set((logs || []).map((l) => l.actor_id).filter(Boolean)));
      if (actorIds.length) {
        const { data: members } = await supabase
          .from("admin_team_members")
          .select("id, auth_user_id, display_name")
          .or(`id.in.(${actorIds.join(",")}),auth_user_id.in.(${actorIds.join(",")})`);
        const actorMap = new Map();
        (members || []).forEach((m) => {
          if (m.auth_user_id) actorMap.set(m.auth_user_id, m.display_name);
          actorMap.set(m.id, m.display_name); // legacy fallback
        });
        setActorNameMap(actorMap);
      } else {
        setActorNameMap(new Map());
      }
      setComments(commentData || []);
      setLoading(false);
    })();
  }, []);


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return `${date.toLocaleString()} (${timeZone})`;
  };

  const parseJsonSafe = (v) => {
    if (!v) return null;
    if (typeof v === "object") return v;
    try { return JSON.parse(v); } catch { return null; }
  };

  const displayActionLabel = (raw) => {
    const key = (raw || "").toUpperCase();
    if (key === "DELETE") return t("home:audit.action.delete", "DELETE");
    if (key === "INSERT") return t("home:audit.action.insert", "INSERT");
    if (key === "UPDATE") return t("home:audit.action.update", "UPDATE");
    if (key === "ADD") return t("home:audit.action.add", "ADD");
    return key || t("home:audit.action.unknown", "UNKNOWN");
  };

  const resolveActionColor = (raw) => {
    const key = (raw || "").toUpperCase();
    const normalized = key === "ADD" ? "INSERT" : key;
    if (normalized === "INSERT") return "success";
    if (normalized === "UPDATE") return "warning";
    if (normalized === "DELETE") return "error";
    return "default";
  };

  const formatAuditLabel = (log) => {
    const table = (log?.table_name || "").toLowerCase();
    const action = (log?.action || "").toLowerCase();
    const after = parseJsonSafe(log?.after_data);
    const before = parseJsonSafe(log?.before_data);
    if (table === "scientific_names") {
      const name = after?.name_spell_valid || before?.name_spell_valid || log?.row_id || t("home:audit.scientificName");
      if (log.row_id) {
        return (
          <>
            {t("home:audit.scientificName")}:{" "}
            <RouterLink to={`/taxonomy/taxon-explore?focus=${encodeURIComponent(log.row_id)}`} style={{ color: "#7FFFD4" }}>
              {name}
            </RouterLink>
          </>
        );
      }
      return `${t("home:audit.scientificName")}: ${name}`;
    }
    if (table === "scientific_name_docs") {
      const doc = after || before || {};
      const taxonId = doc.scientific_name_id || log.row_id;
      const docType = doc.doc_type || t("home:audit.note");
      const taxonName = taxonId ? nameMap.get(taxonId) : null;
      const label = taxonName ? `${docType}: ${taxonName}` : `${docType} (${taxonId})`;
      if (taxonId) {
        return (
          <>
            {t("home:audit.note")}:{" "}
            <RouterLink to={`/taxonomy/taxon-explore?focus=${encodeURIComponent(taxonId)}`} style={{ color: "#7FFFD4" }}>
              {label}
            </RouterLink>
          </>
        );
      }
      return `${t("home:audit.note")}: ${label}`;
    }
    const actionText = displayActionLabel(log.action || t("home:audit.change", "Change"));
    const tableText = log.table_name || t("home:audit.unknown");
    return `${actionText} ${t("home:audit.on", "on")} ${tableText}${log.row_id ? ` (ID: ${log.row_id})` : ""}`;
  };

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  if (loading) {
        return <LoadingScreen />;
  }
  
  return (
    <Box>
      {/* 🎯 Hero Section */}
      <Box
        sx={{
          position: "relative",
          width: "100vw",
          ml: { xs: "calc(-50vw + 50%)", lg: "calc(-50vw + 50%)" }, // Container の左右paddingを打ち消して全幅表示
          height: { xs: "50vh", md: "65vh" },
          backgroundImage: `url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflow: "hidden",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          pb: { xs: 8, md: 10 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0))",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            color: "white",
            textAlign: "center",
            px: 2,
          }}
        >
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: "2rem", md: "3.5rem" }, mb: 1 }}>
              {t("home:hero.title")}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: "0.9rem", md: "1.3rem" } }}>
              {t("home:hero.subtitle")}
            </Typography>
          </motion.div>

          <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <IconButton onClick={scrollToContent} sx={{ color: "white", mt: 2 }}>
              <ExpandMore fontSize="large" />
            </IconButton>
          </motion.div>
        </Box>
      </Box>

      {/* 🌟 Explore the Site Section */}
      <Container sx={{ py: 8 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <Typography variant="h4" textAlign="center" fontWeight="bold" gutterBottom>
            {t("home:gettingStarted.header")}
          </Typography>
          <Divider sx={{ mb: 6 }} />
        </motion.div>

        <Grid container spacing={4} justifyContent="center">
          {[
            { title: t("common:taxonomy"), text: t("home:gettingStarted.taxonomy"), link: "/taxonomy", icon: <AccountTree fontSize="large" /> },
            { title: t("common:analysis"), text: t("home:gettingStarted.analysis"), link: "/analysis", icon: <BarChart fontSize="large" /> },
            { title: t("common:adminTeam"), text: t("home:gettingStarted.adminTeam"), link: "/admin-team", icon: <Group fontSize="large" /> }
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.2 }} viewport={{ once: true }}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                    transition: "0.3s",
                    cursor: "pointer",
                    "&:hover": { boxShadow: 6 },
                  }}
                  onClick={() => navigate(item.link)}
                >
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.text}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* 📝 Recent Data Updates */}
      <Container sx={{ py: 8 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" mb={2}>
            <Update color="primary" />
            <Typography variant="h4" fontWeight="bold">
              {t("home:updates.header")}
            </Typography>
          </Stack>
          <Typography textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
            {t("home:updates.description")}
          </Typography>
          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : auditLogs.length === 0 ? (
          <Typography align="center" color="text.secondary">
            {t("home:updates.noData")}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {auditLogs.map((log, index) => {
              const when = log.changed_at || log.created_at;
              const resolveActorName = () => {
                if (log.actor_id && actorNameMap.has(log.actor_id)) {
                  const disp = actorNameMap.get(log.actor_id);
                  const lang = (i18n.language || "").split("-")[0];
                  return disp?.[lang] || disp?.en || Object.values(disp || {})[0];
                }
                return log.actor_display || t("home:audit.unknown");
              };
              const who = resolveActorName();
              const isBot = who.toLowerCase().includes("bot");
              const whoLabel = (() => {
                if (!isBot) return who;
                if (who.toLowerCase().includes("hierarchical_import")) return t("home:bot.hierarchicalImport");
                return who;
              })();
              const actionLabel = (log.action || "-").toUpperCase();
              const actionColor = resolveActionColor(actionLabel);
              return (
                <motion.div key={index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: index * 0.1 }}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.5}>
                      <Chip size="small" label={when ? formatDate(when) : t("home:audit.unknownDate")} sx={{ height: 22 }} />
                      <Chip size="small" label={displayActionLabel(actionLabel)} color={actionColor} variant="outlined" sx={{ height: 22 }} />
                      <Chip
                        size="small"
                        label={whoLabel}
                        color={isBot ? "default" : "primary"}
                        icon={isBot ? <SmartToyIcon fontSize="small" /> : undefined}
                        sx={{ height: 22, "& .MuiChip-icon": { ml: 0.2 } }}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {formatAuditLabel(log)}
                    </Typography>
                  </Paper>
                </motion.div>
              );
            })}
          </Stack>
        )}

        <Stack alignItems="center" mt={4}>
          <Button variant="outlined" color="primary" size="small" onClick={() => setOpenUpdateDialog(true)}>
            {t("home:updates.seeAll")}
          </Button>
        </Stack>
      </Container>

      {/* 🗨️ Recent Admin Comments */}
      <Container sx={{ py: 8 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" mb={2}>
            <AddComment color="secondary" />
            <Typography variant="h4" fontWeight="bold">
              {t("home:comments.header")}
            </Typography>
          </Stack>
          <Typography textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
            {t("home:comments.description")}
          </Typography>
          <Divider sx={{ mb: 4 }} />
        </motion.div>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : comments.length === 0 ? (
          <Typography align="center" color="text.secondary">
            {t("home:comments.noData")}
          </Typography>
        ) : (
          <Stack spacing={2}>
            {comments.map((comment, index) => (
              <motion.div key={index} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: index * 0.1 }}>
                <Paper elevation={3} sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                    <Avatar sx={{ bgcolor: "primary.main" }}>
                      {comment.username?.charAt(0) || "?"}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {comment.username} - {formatDate(comment.created_at)}
                      </Typography>
                    </Box>
                  </Stack>
                  {(() => {
                    const map = parseContentToMap(comment.content);
                    const displayContent =
                      map[i18n.language] ||
                      map.ja ||
                      map.en ||
                      Object.values(map)[0] ||
                      "";
                    return (
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "normal" }}
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayContent) }}
                      />
                    );
                  })()}
                </Paper>
              </motion.div>
            ))}
          </Stack>
        )}

        <Stack alignItems="center" mt={4}>
          <Button variant="outlined" color="secondary" size="small" onClick={() => setOpenCommentDialog(true)}>
            {t("home:comments.seeAll")}
          </Button>
        </Stack>
      </Container>

      {/* ダイアログ呼び出し */}
      <CommentDialog open={openCommentDialog} onClose={() => setOpenCommentDialog(false)} />
      <UpdateDialog open={openUpdateDialog} onClose={() => setOpenUpdateDialog(false)} />
    </Box>
  );
}
